// React / Next
import { Link } from '@tanstack/react-router';

// Components
import {
  PortableText,
  type PortableTextComponents,
  type PortableTextBlock,
} from '@portabletext/react';

// Utils
import { cn } from '../../utils/cn';

// Types
type ArticlePortableTextProps = {
  value: PortableTextBlock[];
  className?: string;
};

/**
 * Custom components for article Portable Text rendering
 * Styled according to Figma design specs
 */
const components: PortableTextComponents = {
  block: {
    normal: ({ children }) => (
      <p
        className="font-body text-blue-1000"
        style={{
          fontSize: '16px',
          lineHeight: '24px',
          marginBottom: '18px',
        }}
      >
        {children}
      </p>
    ),
    h2: ({ children }) => (
      <h2
        className="font-heading font-light text-blue-1000"
        style={{
          fontSize: '40px',
          lineHeight: '48px',
          letterSpacing: '-0.5px',
          marginTop: '48px',
          marginBottom: '12px',
        }}
      >
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3
        className="font-heading font-light text-blue-1000"
        style={{
          fontSize: '32px',
          lineHeight: '40px',
          marginTop: '32px',
          marginBottom: '16px',
        }}
      >
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4
        className="font-heading font-light text-blue-1000"
        style={{
          fontSize: '24px',
          lineHeight: '32px',
          marginTop: '24px',
          marginBottom: '16px',
        }}
      >
        {children}
      </h4>
    ),
    blockquote: ({ children }) => (
      <blockquote
        className="font-heading font-light text-blue-800 border-l-4 border-orange-800"
        style={{
          fontSize: '20px',
          lineHeight: '28px',
          paddingLeft: '24px',
          margin: '32px 0',
        }}
      >
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul
        className="flex flex-col"
        style={{ gap: '12px', marginBottom: '24px' }}
      >
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol
        className="flex flex-col list-decimal list-inside"
        style={{ gap: '12px', marginBottom: '24px' }}
      >
        {children}
      </ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => (
      <li
        className="font-body text-blue-1000 list-disc"
        style={{
          fontSize: '16px',
          lineHeight: '24px',
          marginLeft: '24px',
        }}
      >
        {children}
      </li>
    ),
    number: ({ children }) => (
      <li
        className="font-body text-blue-1000"
        style={{
          fontSize: '16px',
          lineHeight: '24px',
        }}
      >
        {children}
      </li>
    ),
  },
  marks: {
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    link: ({ children, value }) => {
      const href = value?.href || '';
      const isExternal = href.startsWith('http');
      const openInNewTab = value?.openInNewTab || isExternal;

      if (isExternal) {
        return (
          <a
            href={href}
            className="text-blue-800 underline underline-offset-2 hover:text-orange-800 transition-colors"
            target={openInNewTab ? '_blank' : undefined}
            rel={openInNewTab ? 'noopener noreferrer' : undefined}
          >
            {children}
          </a>
        );
      }

      return (
        <Link
          to={href}
          className="text-blue-800 underline underline-offset-2 hover:text-orange-800 transition-colors"
        >
          {children}
        </Link>
      );
    },
  },
  types: {
    image: ({ value }) => {
      if (!value?.asset?.url) return null;
      return (
        <figure style={{ margin: '32px 0' }}>
          <img
            src={value.asset.url}
            alt={value.alt || ''}
            className="w-full object-cover"
            style={{ borderRadius: '12px' }}
          />
          {value.caption && (
            <figcaption
              className="font-body text-gray-600 text-center"
              style={{
                fontSize: '14px',
                lineHeight: '20px',
                marginTop: '8px',
              }}
            >
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
  },
};

/**
 * ArticlePortableText - Renders Sanity Portable Text content for articles
 * Styled according to the design system with proper typography and spacing
 */
export const ArticlePortableText = ({ value, className }: ArticlePortableTextProps) => {
  if (!value || value.length === 0) return null;

  return (
    <article className={cn('w-full', className)}>
      <PortableText value={value} components={components} />
    </article>
  );
};

